import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiCheckCircle, FiEdit, FiEye, FiTrash2, FiXCircle } from "react-icons/fi";
import { IoMdSearch } from "react-icons/io";
import { TbTableColumn } from "react-icons/tb";
import moment from "moment";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import StatusChip, { statusChipType } from "../../../../Common/StatusChip";
import ActionButton from "../../../../Common/ActionButton";
import { filterStoreProjects } from "../../../projectFilters";
import {
  GetAllPurchaseInvoicesApi,
  GetPurchaseInvoiceByIdApi,
  PostPurchaseInvoiceApi,
  DeletePurchaseInvoiceApi,
  CancelPurchaseInvoiceApi,
} from "../../../../../Api/Operations/PurchaseInvoices";

// Backend statuses are Draft / Posted / Cancelled; statusChipType does not know "Posted".
const invoiceChipType = (status) => (status === "Posted" ? "success" : statusChipType(status));

const ACTION_TEXT = {
  post: {
    title: "Post invoice",
    body: "Posting creates the accounting entries and the invoice can no longer be edited. Continue?",
    confirm: "Post",
  },
  delete: {
    title: "Delete invoice",
    body: "This draft invoice will be deleted permanently. Continue?",
    confirm: "Delete",
  },
  cancel: {
    title: "Cancel invoice",
    body: "Cancelling posts reversing entries and frees the billed quantities.",
    confirm: "Cancel invoice",
  },
};

/**
 * Purchase Invoice Table Component
 * Route: /procurement/purchase-invoice
 */
export default function PurchaseInvoiceTable() {
  const history = useHistory();
  const rolesData = useSelector((state) => state.GetAllRole);
  const projects = useMemo(() => filterStoreProjects(rolesData?.data?.Projects) || [], [rolesData]);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [colDropdownOpen, setColDropdownOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selectedInvoiceForView, setSelectedInvoiceForView] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [pendingAction, setPendingAction] = useState(null); // { type, id, number }
  const [cancelReason, setCancelReason] = useState("");
  const [isActing, setIsActing] = useState(false);

  const gridRef = useRef(null);
  const colDropdownRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    GetAllPurchaseInvoicesApi(selectedProjectId).then((res) => {
      if (cancelled) {
        return;
      }
      setIsLoading(false);
      if (res?.data?.IsSuccess) {
        setInvoices(res.data.Data || []);
      } else {
        setInvoices([]);
        toast.error(res?.data?.Message || "Failed to load purchase invoices");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, reloadKey]);

  const openView = useCallback(async (id) => {
    const res = await GetPurchaseInvoiceByIdApi(id);
    if (res?.data?.IsSuccess) {
      setSelectedInvoiceForView(res.data.Data);
    } else {
      toast.error(res?.data?.Message || "Failed to load purchase invoice");
    }
  }, []);

  const runAction = async () => {
    const { type, id } = pendingAction;
    if (type === "cancel" && !cancelReason.trim()) {
      toast.error("A cancellation reason is required");
      return;
    }
    setIsActing(true);
    try {
      let res;
      if (type === "post") {
        res = await PostPurchaseInvoiceApi(id);
      } else if (type === "delete") {
        res = await DeletePurchaseInvoiceApi(id);
      } else {
        res = await CancelPurchaseInvoiceApi(id, cancelReason.trim());
      }
      if (res?.data?.IsSuccess) {
        toast.success(res.data.Message || "Done");
        setPendingAction(null);
        setReloadKey((k) => k + 1);
      } else {
        toast.error(res?.data?.Message || "Action failed");
      }
    } finally {
      setIsActing(false);
    }
  };

  // Filtered dataset
  const filteredData = useMemo(() => {
    return invoices.filter((inv) => {
      const matchProject =
        !selectedProjectId || String(inv.ProjectId) === String(selectedProjectId);
      const matchSearch =
        !searchTerm ||
        inv.InvoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.VendorInvoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.VendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.PoNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.Grns || []).join(", ").toLowerCase().includes(searchTerm.toLowerCase());
      return matchProject && matchSearch;
    });
  }, [invoices, selectedProjectId, searchTerm]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
    }),
    [],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Invoice #",
        field: "InvoiceNumber",
        width: 140,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 600, color: "#0b6462" }}>{params.value}</span>
        ),
      },
      {
        headerName: "Vendor Bill #",
        field: "VendorInvoiceNo",
        width: 130,
      },
      {
        headerName: "Vendor",
        field: "VendorName",
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: "Project",
        field: "ProjectName",
        width: 140,
      },
      {
        headerName: "Linked PO",
        field: "PoNumber",
        width: 130,
      },
      {
        headerName: "Linked GRN",
        valueGetter: (p) => (p.data?.Grns || []).join(", "),
        width: 130,
      },
      {
        headerName: "Invoice Date",
        field: "InvoiceDate",
        width: 110,
        valueFormatter: (p) => (p.value ? moment(p.value).format("YYYY-MM-DD") : "N/A"),
      },
      {
        headerName: "Due Date",
        field: "DueDate",
        width: 110,
        valueFormatter: (p) => (p.value ? moment(p.value).format("YYYY-MM-DD") : "N/A"),
      },
      {
        headerName: "Grand Total",
        field: "GrandTotal",
        width: 140,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#107c41" }}>
            Rs. {(Number(p.value) || 0).toLocaleString()}
          </span>
        ),
      },
      {
        headerName: "Paid",
        field: "PaidAmount",
        width: 110,
        valueFormatter: (p) =>
          p.data?.Status === "Posted" ? (Number(p.value) || 0).toLocaleString() : "",
      },
      {
        headerName: "Outstanding",
        field: "Outstanding",
        width: 120,
        valueFormatter: (p) =>
          p.data?.Status === "Posted" ? (Number(p.value) || 0).toLocaleString() : "",
      },
      {
        headerName: "Payment",
        field: "PaymentStatus",
        width: 110,
        valueFormatter: (p) => (p.data?.Status === "Posted" ? p.value || "Unpaid" : ""),
      },
      {
        headerName: "Status",
        field: "Status",
        width: 130,
        cellRenderer: (params) => {
          if (!params.value) {
            return "N/A";
          }
          return <StatusChip status={params.value} type={invoiceChipType(params.value)} />;
        },
      },
      {
        headerName: "Action",
        colId: "action",
        width: 150,
        filter: false,
        sortable: false,
        headerComponent: () => (
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <ActionButton
              text="New+"
              newBtn="true"
              onClick={() => history.push("/procurement/purchase-invoice/add")}
            />
          </div>
        ),
        cellRenderer: (params) => {
          const row = params.data;
          const iconStyle = { cursor: "pointer", color: "#0b6462" };
          const ask = (type) => {
            setCancelReason("");
            setPendingAction({ type, id: row.Id, number: row.InvoiceNumber });
          };
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: "8px",
              }}
            >
              <FiEye
                title="View Invoice Details"
                size="1.2em"
                style={iconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  openView(row.Id);
                }}
              />
              {row.Status === "Draft" && (
                <>
                  <FiEdit
                    title="Edit"
                    size="1.2em"
                    style={iconStyle}
                    onClick={() =>
                      history.push("/procurement/purchase-invoice/add", { Id: row.Id })
                    }
                  />
                  <FiCheckCircle
                    title="Post"
                    size="1.2em"
                    style={{ ...iconStyle, color: "#107c41" }}
                    onClick={() => ask("post")}
                  />
                  <FiTrash2
                    title="Delete"
                    size="1.2em"
                    style={{ ...iconStyle, color: "#a71d2a" }}
                    onClick={() => ask("delete")}
                  />
                </>
              )}
              {row.Status === "Posted" && (
                <FiXCircle
                  title="Cancel"
                  size="1.2em"
                  style={{ ...iconStyle, color: "#a71d2a" }}
                  onClick={() => ask("cancel")}
                />
              )}
            </div>
          );
        },
      },
    ],
    [history, openView],
  );

  const onGridReady = useCallback((params) => {
    const visibilityMap = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        visibilityMap[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(visibilityMap);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isVisible);
      setColumnVisibility((prev) => ({
        ...prev,
        [colId]: !isVisible,
      }));
    },
    [columnVisibility],
  );

  return (
    <>
      {/* Top Toolbar */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Purchase Invoices"
        >
          Purchase Invoices
        </div>
        {/* Search */}
        <div className="d-flex align-items-center max-w-250" style={{ position: "relative" }}>
          <IoMdSearch
            size="1.1em"
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#4e545a",
            }}
          />
          <input
            type="text"
            className="input_styling"
            placeholder="Search Invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "250px", paddingLeft: 30 }}
            autoComplete="off"
          />
        </div>

        {/* Project Filter */}
        <select
          className="input_styling"
          style={{ marginLeft: "10px", width: "220px" }}
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.ProjectId} value={p.ProjectId}>
              {p.ProjectName}
            </option>
          ))}
        </select>

        {/* Column Visibility */}
        <div style={{ position: "relative", marginLeft: "10px" }} ref={colDropdownRef}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center"
            style={{
              height: "36px",
              padding: "0 10px",
              borderRadius: "6px",
              borderColor: "#ced4da",
            }}
            onClick={() => setColDropdownOpen((prev) => !prev)}
            title="Toggle Columns"
          >
            <TbTableColumn size="1.2em" style={{ marginRight: 4 }} />
            <span>Columns</span>
          </button>
          {colDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                zIndex: 1050,
                background: "#fff",
                border: "1px solid #ced4da",
                borderRadius: "6px",
                padding: "8px 12px",
                minWidth: "180px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              {Object.keys(columnVisibility).map((colId) => (
                <div key={colId} className="form-check" style={{ marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`col_${colId}`}
                    checked={columnVisibility[colId] !== false}
                    onChange={() => toggleColumn(colId)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`col_${colId}`}
                    style={{ fontSize: "12px", cursor: "pointer" }}
                  >
                    {colId}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AG Grid Table */}
      <div
        className="ag-theme-alpine mt-3"
        style={{
          width: "100%",
          height: "calc(100vh - 220px)",
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={filteredData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={15}
          onGridReady={onGridReady}
          rowSelection="single"
          animateRows={true}
          overlayNoRowsTemplate={
            isLoading ? "Loading purchase invoices..." : "No purchase invoices found"
          }
        />
      </div>

      {/* Invoice Details Modal */}
      {selectedInvoiceForView && (
        <Modal
          show={Boolean(selectedInvoiceForView)}
          onHide={() => setSelectedInvoiceForView(null)}
          size="lg"
          centered
        >
          <Modal.Header closeButton style={{ borderBottom: "1px solid #e2e8f0" }}>
            <Modal.Title style={{ fontSize: "17px", fontWeight: 700, color: "#1e293b" }}>
              Purchase Invoice: {selectedInvoiceForView.InvoiceNumber}{" "}
              <StatusChip
                status={selectedInvoiceForView.Status}
                type={invoiceChipType(selectedInvoiceForView.Status)}
              />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: "13px" }}>
            <div className="row mb-3">
              <div className="col-md-4">
                <strong>Vendor:</strong> {selectedInvoiceForView.VendorName}
              </div>
              <div className="col-md-4">
                <strong>Vendor Bill #:</strong> {selectedInvoiceForView.VendorInvoiceNo}
              </div>
              <div className="col-md-4">
                <strong>Project:</strong> {selectedInvoiceForView.ProjectName}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-4">
                <strong>Linked PO:</strong> {selectedInvoiceForView.PoNumber}
              </div>
              <div className="col-md-4">
                <strong>Linked GRN:</strong> {(selectedInvoiceForView.Grns || []).join(", ")}
              </div>
              <div className="col-md-4">
                <strong>Invoice Date:</strong>{" "}
                {moment(selectedInvoiceForView.InvoiceDate).format("YYYY-MM-DD")}
              </div>
            </div>

            {selectedInvoiceForView.File && (
              <div className="mb-3">
                <strong>Attachment:</strong>{" "}
                <a href={selectedInvoiceForView.File} target="_blank" rel="noopener noreferrer">
                  {selectedInvoiceForView.FileName || "Preview file"}
                </a>
              </div>
            )}

            {selectedInvoiceForView.CancelledReason && (
              <div className="mb-3">
                <strong>Cancelled Reason:</strong> {selectedInvoiceForView.CancelledReason}
              </div>
            )}

            <h6 className="font-weight-bold mt-4 mb-2">3-Way Match Verification Items</h6>
            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead style={{ backgroundColor: "#f8fafc" }}>
                  <tr>
                    <th>GRN</th>
                    <th>Material</th>
                    <th>Packaging/Unit</th>
                    <th className="text-center">Accepted Qty</th>
                    <th className="text-center">Billed Qty</th>
                    <th className="text-right">PO Rate</th>
                    <th className="text-right">Billed Rate</th>
                    <th className="text-right">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoiceForView.Lines || []).map((l, i) => (
                    <tr key={l.Id || i}>
                      <td>{l.GrnNumber}</td>
                      <td>{l.Material}</td>
                      <td>{l.Unit}</td>
                      <td className="text-center">{l.AcceptedQuantity}</td>
                      <td className="text-center">{l.BilledQuantity}</td>
                      <td className="text-right">Rs. {Number(l.PoRate).toLocaleString()}</td>
                      <td className="text-right">Rs. {Number(l.BilledRate).toLocaleString()}</td>
                      <td className="text-right font-weight-bold">
                        Rs. {(Number(l.LineTotal) || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row justify-content-end mt-3">
              <div className="col-md-6">
                <div className="d-flex justify-content-between mb-1">
                  <span>Subtotal:</span>
                  <strong>Rs. {selectedInvoiceForView.ItemsSubtotal?.toLocaleString()}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Freight / Delivery:</span>
                  <strong>Rs. {selectedInvoiceForView.FreightAmount?.toLocaleString()}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Sales Tax / GST:</span>
                  <strong>Rs. {selectedInvoiceForView.TaxAmount?.toLocaleString()}</strong>
                </div>
                <hr className="my-2" />
                <div
                  className="d-flex justify-content-between font-weight-bold"
                  style={{ fontSize: "16px", color: "#107c41" }}
                >
                  <span>Grand Total:</span>
                  <span>Rs. {selectedInvoiceForView.GrandTotal?.toLocaleString()}</span>
                </div>
                {selectedInvoiceForView.Status === "Posted" && (
                  <>
                    <div className="d-flex justify-content-between mt-1">
                      <span>Paid:</span>
                      <strong>
                        Rs. {(selectedInvoiceForView.PaidAmount ?? 0).toLocaleString()}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mt-1">
                      <span>Outstanding:</span>
                      <strong>
                        Rs. {(selectedInvoiceForView.Outstanding ?? 0).toLocaleString()}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mt-1">
                      <span>Payment Status:</span>
                      <strong>{selectedInvoiceForView.PaymentStatus || "Unpaid"}</strong>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedInvoiceForView(null)}
            >
              Close
            </button>
            {selectedInvoiceForView.Status === "Draft" && (
              <button
                className="btn btn-info btn-sm text-white"
                onClick={() => {
                  history.push("/procurement/purchase-invoice/add", {
                    Id: selectedInvoiceForView.Id,
                  });
                }}
              >
                Edit in Form
              </button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {/* Post / Delete / Cancel confirmation */}
      {pendingAction && (
        <Modal show onHide={() => !isActing && setPendingAction(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "17px", fontWeight: 700 }}>
              {ACTION_TEXT[pendingAction.type].title}: {pendingAction.number}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: "13px" }}>
            <p>{ACTION_TEXT[pendingAction.type].body}</p>
            {pendingAction.type === "cancel" && (
              <>
                <label className="input_label">Reason (required):</label>
                <textarea
                  className="input_styling w-100"
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <ActionButton
              text="Close"
              variant="cancel"
              onClick={() => setPendingAction(null)}
              disabled={isActing}
            />
            <ActionButton
              text={ACTION_TEXT[pendingAction.type].confirm}
              onClick={runAction}
              disabled={isActing}
              isLoading={isActing}
            />
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
